import { useEffect, useId, useRef } from 'react';

import type { ReactNode } from 'react';

/**
 * A modal dialog, built on the native `<dialog>` element.
 *
 * `showModal()` is doing real work here, not just showing a box: the platform
 * gives us the focus trap, the inert background, Escape-to-close and the
 * backdrop for free. A hand-rolled overlay would need all four reimplemented,
 * and the one everybody skips is the focus trap — which is what makes a dialog
 * usable from the keyboard at all.
 *
 * The two places it needs help:
 *
 * - **Escape.** The platform closes the element directly, without telling React.
 *   Listening for `cancel` and routing it through `onClose` keeps the element's
 *   open state and ours from disagreeing — otherwise the dialog is closed on
 *   screen but still "open" in state, and the trigger appears dead.
 * - **Backdrop clicks.** `::backdrop` is not a node, so a click on it targets
 *   the `<dialog>` itself. Comparing the target is how the two are told apart.
 * - **Initial focus.** React's `autoFocus` prop is a no-op in here: it calls
 *   `.focus()` during mount, when the dialog is still `display: none` because
 *   `showModal()` has not run. Without an `autofocus` *attribute* to honour,
 *   the platform then parks focus on the scroll container, and an operator's
 *   first keystrokes go nowhere. Focusing `[data-autofocus]` after opening is
 *   the fix, because that is the first moment the field can take focus.
 */
export function Modal({
    open,
    onClose,
    title,
    description,
    footer,
    children,
    dismissable = true,
}: {
    open: boolean;
    onClose: () => void;
    title: string;
    description?: ReactNode;
    footer?: ReactNode;
    children: ReactNode;
    /**
     * Pass `false` while a write is in flight. The footer buttons disable
     * themselves, but Escape and a backdrop click would still reach `onClose`
     * — and closing a dialog mid-mutation resets it, so the write can succeed
     * server-side with nobody told and no `onSuccess` follow-through.
     */
    dismissable?: boolean;
}) {
    const ref = useRef<HTMLDialogElement>(null);
    // Generated, not the literal "modal-title" this used to hard-code: two
    // dialogs mounted at once would emit duplicate ids, and `aria-labelledby`
    // resolves to the first match — so one dialog would announce the other's
    // heading. Both flag dialogs are always mounted, just closed.
    const titleId = useId();

    useEffect(() => {
        const dialog = ref.current;
        if (!dialog) return;

        if (open && !dialog.open) {
            dialog.showModal();
            dialog.querySelector<HTMLElement>('[data-autofocus]')?.focus();
        }
        if (!open && dialog.open) dialog.close();
    }, [open]);

    return (
        <dialog
            ref={ref}
            aria-labelledby={titleId}
            onCancel={(event) => {
                event.preventDefault();
                if (dismissable) onClose();
            }}
            onClick={(event) => {
                if (dismissable && event.target === ref.current) onClose();
            }}
            className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-0 shadow-card backdrop:bg-gray-900/40"
        >
            {/* The dialog is the click target for the backdrop, so inner clicks
                must not bubble back up to it and close the thing being used. */}
            <div
                onClick={(event) => {
                    event.stopPropagation();
                }}
            >
                <div className="border-b border-gray-100 px-5 py-4">
                    <h2 id={titleId} className="text-[15px] font-semibold text-gray-900">
                        {title}
                    </h2>
                    {description && (
                        <p className="mt-1 text-[12.5px] leading-relaxed text-gray-500">
                            {description}
                        </p>
                    )}
                </div>

                <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>

                {footer && (
                    <div className="flex items-center justify-end gap-2.5 border-t border-gray-100 bg-gray-50 px-5 py-3.5">
                        {footer}
                    </div>
                )}
            </div>
        </dialog>
    );
}
